import { useCallback, useEffect, useMemo, useState } from 'react';
import { Loader2, ShieldAlert } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import {
  LwButton,
  LwEmpty,
  LwPageHeader,
  LwSearchInput,
  LwSegmented,
  LwTable,
  LwTableToolbar,
  LwThemeScope
} from '@/components/lw';
import { FraudAlertStatusPill } from '@/components/fraud-alerts/FraudAlertStatusPill';
import {
  getFraudAlertReportRows,
  type FraudAlertReportRow
} from '@/services/fraudAlertService';

type StatusFilter = 'all' | 'open' | 'reviewed';

const STATUS_ITEMS = [
  { value: 'all', label: 'All' },
  { value: 'open', label: 'Open' },
  { value: 'reviewed', label: 'Reviewed' }
] as const;

function formatDateTime(value: string | null): string {
  if (!value) {
    return '—';
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return '—';
  }
  return new Intl.DateTimeFormat('en-US', {
    dateStyle: 'medium',
    timeStyle: 'short'
  }).format(date);
}

function evidencePageLabel(row: FraudAlertReportRow): string {
  const { alert } = row;
  if (alert.evidencePages.length === 0) {
    return alert.detectorVersion === 'legacy'
      ? 'Original alert page data unavailable'
      : 'No evidence page recorded';
  }
  const prefix = alert.detectorVersion === 'legacy' ? 'Reported page' : 'Evidence page';
  return `${prefix}${alert.evidencePages.length === 1 ? '' : 's'} ${alert.evidencePages.join(', ')}`;
}

export function FraudAlertsReport() {
  const navigate = useNavigate();
  const [rows, setRows] = useState<FraudAlertReportRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');

  const loadRows = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      setRows(await getFraudAlertReportRows());
    } catch (error) {
      setRows([]);
      setLoadError(error instanceof Error ? error.message : 'Unable to load fraud alerts.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadRows();
  }, [loadRows]);

  const filteredRows = useMemo(() => {
    const query = search.trim().toLowerCase();
    return rows
      .filter(row => {
        const isOpen = row.alert.reviewStatus === 'open';
        if (statusFilter === 'open' && !isOpen) {
          return false;
        }
        if (statusFilter === 'reviewed' && isOpen) {
          return false;
        }
        if (!query) {
          return true;
        }
        const searchable = [
          row.loan?.loanNumber,
          row.loan?.borrowerName,
          row.loan?.loanOfficerName,
          row.loan?.processorName,
          row.alert.documentName,
          row.alert.documentFolder,
          ...row.alert.indicators
        ].filter(Boolean).join(' ').toLowerCase();
        return searchable.includes(query);
      })
      .sort((left, right) => {
        const leftOpen = left.alert.reviewStatus === 'open' ? 0 : 1;
        const rightOpen = right.alert.reviewStatus === 'open' ? 0 : 1;
        if (leftOpen !== rightOpen) {
          return leftOpen - rightOpen;
        }
        return Date.parse(right.alert.detectedAt) - Date.parse(left.alert.detectedAt);
      });
  }, [rows, search, statusFilter]);

  const openCount = rows.filter(row => row.alert.reviewStatus === 'open').length;

  return (
    <LwThemeScope className="lw-ds-page">
      <div className="wrap space-y-5">
        <LwPageHeader
          eyebrow="Company Reports"
          title={<>Fraud <em>Alerts</em></>}
          actions={(
            <div className="inline-flex items-center gap-2 rounded-full border border-destructive/20 bg-destructive/10 px-3 py-1.5 text-sm font-semibold text-destructive">
              <ShieldAlert className="h-4 w-4" aria-hidden="true" />
              {openCount} open
            </div>
          )}
        />

        <p className="text-sm text-muted-foreground">
          One row per amended-return alert. Open items appear first; access remains limited to loans you can view.
        </p>

        <LwTable
          presentation="canonical"
          density="compact"
          ariaLabel="Company fraud alerts"
          toolbar={(
            <LwTableToolbar
              label="Fraud alert filters"
              primary={(
                <LwSearchInput
                  value={search}
                  onChange={setSearch}
                  placeholder="Search loan, borrower, LO, or processor"
                  ariaLabel="Search fraud alerts"
                />
              )}
              actions={(
                <LwSegmented
                  mode="single-select"
                  ariaLabel="Fraud alert status"
                  value={statusFilter}
                  items={STATUS_ITEMS}
                  onValueChange={setStatusFilter}
                />
              )}
            />
          )}
          footer={!loading && !loadError ? (
            <span>Showing <strong>{filteredRows.length}</strong> of <strong>{rows.length}</strong> alerts</span>
          ) : undefined}
        >
          <thead>
            <tr>
              <th scope="col">Detected</th>
              <th scope="col">Loan / Borrower</th>
              <th scope="col">Alert</th>
              <th scope="col">Status</th>
              <th scope="col">LO</th>
              <th scope="col">Processor</th>
              <th scope="col">Email Sent</th>
              <th scope="col" className="is-right">Action</th>
            </tr>
          </thead>
          {!loading && !loadError && filteredRows.length > 0 && (
            <tbody>
              {filteredRows.map(row => (
                <tr key={row.alert.id}>
                  <td className="whitespace-nowrap align-top">{formatDateTime(row.alert.detectedAt)}</td>
                  <td className="min-w-52 align-top">
                    <div className="font-medium text-foreground">{row.loan?.loanNumber || 'Unknown loan'}</div>
                    <div className="mt-1 text-xs text-muted-foreground">
                      {row.loan?.borrowerName || 'Unknown borrower'}
                    </div>
                  </td>
                  <td className="min-w-56 align-top">
                    <div className="font-medium text-foreground">
                      {row.alert.documentName || row.alert.indicators[0] || 'Amended return detected'}
                    </div>
                    {row.alert.documentName && row.alert.indicators.length > 0 && (
                      <div className="mt-1 text-xs text-foreground">
                        {row.alert.indicators.join(' · ')}
                      </div>
                    )}
                    <div className="mt-1 text-xs text-muted-foreground">
                      {evidencePageLabel(row)}
                    </div>
                  </td>
                  <td className="align-top"><FraudAlertStatusPill status={row.alert.reviewStatus} /></td>
                  <td className="align-top">{row.loan?.loanOfficerName || 'Unassigned'}</td>
                  <td className="align-top">{row.loan?.processorName || 'Unassigned'}</td>
                  <td className="whitespace-nowrap align-top">
                    {row.alert.notified ? formatDateTime(row.alert.notifiedAt) : 'Not sent'}
                  </td>
                  <td className="text-right align-top">
                    {row.loan ? (
                      <LwButton
                        variant="sm-solid"
                        onClick={() => navigate(
                          `/internal?section=loan-center&loan=${encodeURIComponent(row.loan!.id)}&tab=fraudAlerts`
                        )}
                      >
                        Open loan
                      </LwButton>
                    ) : (
                      <span className="text-xs text-muted-foreground">Loan unavailable</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          )}
        </LwTable>

        {loading && (
          <div className="flex items-center justify-center gap-2 py-16 text-muted-foreground" role="status">
            <Loader2 className="h-5 w-5 animate-spin" aria-hidden="true" />
            Loading fraud alerts…
          </div>
        )}
        {!loading && loadError && (
          <div className="rounded-lg border border-destructive/20 bg-destructive/5 p-6" role="alert">
            <p className="font-medium text-destructive">Fraud alerts could not be loaded.</p>
            <p className="mt-1 text-sm text-muted-foreground">{loadError}</p>
            <LwButton className="mt-4" variant="sm" onClick={() => void loadRows()}>
              Try again
            </LwButton>
          </div>
        )}
        {!loading && !loadError && filteredRows.length === 0 && (
          <LwEmpty title={rows.length === 0 ? 'No fraud alerts' : 'No alerts match'}>
            {rows.length === 0
              ? 'No amended-return alerts are available for loans you can view.'
              : 'Adjust the search or status filter.'}
          </LwEmpty>
        )}
      </div>
    </LwThemeScope>
  );
}
